import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, ArrowLeft, Send, Sparkles, ChevronLeft, Plus, Search, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;
const ROLE_LABEL = { admin: "Admin", coach: "Coach", mentor: "Mentor", student: "Siswa" };

function Avatar({ name, picture, size = 10 }) {
  const cls = `w-${size} h-${size} rounded-full object-cover flex-shrink-0`;
  if (picture) return <img src={toAbsUrl(picture)} alt="" className={cls} />;
  return (
    <div className={`w-${size} h-${size} rounded-full bg-[#F4F2F8] flex items-center justify-center text-[#FF8C00] font-bold text-sm flex-shrink-0`}>
      {name?.[0] || "?"}
    </div>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mobileView, setMobileView] = useState("list");
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/me/conversations");
      setThreads(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThreads(); }, []);

  const openThread = async (userId) => {
    setActive(userId);
    setMobileView("chat");
    const { data } = await api.get(`/me/conversations/${userId}`);
    setMessages(data.messages);
    setOther(data.other);
    setThreads(prev => prev.map(t => t.user_id === userId ? { ...t, unread: 0 } : t));
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  useEffect(() => {
    if (active) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/me/conversations/${active}`, { content: input.trim() });
      setMessages(prev => [...prev, data]);
      setInput("");
      loadThreads();
    } catch (e) {
      alert(e.response?.data?.detail || "Gagal mengirim");
    } finally {
      setSending(false);
    }
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  const openNewMsg = async () => {
    setShowNewMsg(true);
    setUserSearch("");
    if (userList.length === 0) {
      try {
        const { data } = await api.get("/me/messageable-users");
        setUserList(data);
      } catch {}
    }
  };

  const startConversation = (u) => {
    setShowNewMsg(false);
    openThread(u.user_id);
  };

  const filteredUsers = userList.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const ROLE_LABEL_MAP = { admin: "Admin", coach: "Coach", mentor: "Mentor", advisor: "Advisor", student: "Siswa" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="inbox-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5C5868] hover:text-[#1A1422] mb-5">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <Mail className="w-6 h-6 text-[#FF8C00]" />
        <h1 className="font-heading text-2xl font-extrabold text-[#1A1422]">Kotak Pesan</h1>
        {totalUnread > 0 && (
          <span className="text-xs bg-[#FF8C00] text-white px-2 py-0.5 rounded-full font-bold">{totalUnread}</span>
        )}
      </div>

      <div className="card-surface overflow-hidden" style={{ minHeight: 480 }}>
        <div className="flex h-full" style={{ minHeight: 480 }}>

          {/* Thread list */}
          <div className={`border-r border-[#E8E5EE] flex flex-col ${mobileView === "chat" ? "hidden md:flex" : "flex"} w-full md:w-72 flex-shrink-0`}>
            {/* Tombol Pesan Baru */}
            <div className="p-3 border-b border-[#E8E5EE]">
              <button onClick={openNewMsg} className="btn-primary w-full justify-center text-sm py-2">
                <Plus className="w-4 h-4" /> Pesan Baru
              </button>
            </div>
            {loading ? (
              <div className="p-6 text-sm text-[#8B8794]">Memuat...</div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center">
                <Sparkles className="w-8 h-8 text-[#FF8C00] mx-auto mb-2" />
                <p className="text-sm text-[#8B8794]">Belum ada percakapan.</p>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1">
                {threads.map(t => (
                  <button
                    key={t.user_id}
                    onClick={() => openThread(t.user_id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-[#F4F2F8] hover:bg-[#FAF9FC] transition-colors ${active === t.user_id ? "bg-[#FFF4E6]" : ""}`}
                  >
                    <div className="relative flex-shrink-0">
                      <Avatar name={t.name} picture={t.picture} size={10} />
                      {t.unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#FF8C00] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{t.unread}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${t.unread > 0 ? "font-semibold text-[#1A1422]" : "text-[#1A1422]"}`}>{t.name}</div>
                      <div className="text-xs text-[#8B8794] truncate mt-0.5">{t.last_message}</div>
                    </div>
                    <div className="text-[10px] text-[#8B8794] flex-shrink-0">
                      {new Date(t.last_at).toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Chat panel */}
          <div className={`flex flex-col flex-1 ${mobileView === "list" ? "hidden md:flex" : "flex"}`}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-[#8B8794] text-sm">
                Pilih percakapan untuk dibuka
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8E5EE] flex-shrink-0">
                  <button onClick={() => setMobileView("list")} className="md:hidden text-[#5C5868]">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  {other && <Avatar name={other.name} picture={other.picture} size={9} />}
                  <div>
                    <div className="font-semibold text-sm text-[#1A1422]">{other?.name}</div>
                    {other?.role && <div className="text-xs text-[#8B8794]">{ROLE_LABEL[other.role] || other.role}</div>}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" style={{ maxHeight: 360 }}>
                  {messages.length === 0 ? (
                    <div className="text-center text-sm text-[#8B8794] mt-10">Belum ada pesan. Mulai percakapan.</div>
                  ) : messages.map(m => {
                    const isMe = m.from_user_id === user?.user_id;
                    return (
                      <div key={m.message_id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                        <Avatar name={m.from_name} picture={m.from_picture} size={7} />
                        <div className={`max-w-[72%] flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${isMe ? "bg-[#FF8C00] text-white rounded-tr-sm" : "bg-[#F4F2F8] text-[#1A1422] rounded-tl-sm"}`}>
                            {m.content}
                          </div>
                          <span className="text-[10px] text-[#8B8794] mt-1">
                            {new Date(m.created_at).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div className="border-t border-[#E8E5EE] p-3 flex gap-2 flex-shrink-0">
                  <input
                    className="flex-1 input-field text-sm py-2 px-3"
                    placeholder="Tulis pesan..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
                  />
                  <button
                    onClick={send}
                    disabled={!input.trim() || sending}
                    className="btn-primary px-3 py-2 disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Modal Pesan Baru */}
      {showNewMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowNewMsg(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-[#1A1422]">Pesan Baru</h3>
              <button onClick={() => setShowNewMsg(false)} className="p-1.5 hover:bg-[#F4F2F8] rounded-full">
                <X className="w-4 h-4 text-[#5C5868]" />
              </button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8794]" />
              <input
                autoFocus
                className="input-field pl-9 text-sm py-2"
                placeholder="Cari nama..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-[#8B8794] text-center py-4">Tidak ada pengguna ditemukan.</p>
              ) : filteredUsers.map(u => (
                <button key={u.user_id} onClick={() => startConversation(u)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FAF9FC] text-left transition-colors">
                  <Avatar name={u.name} picture={u.picture} size={9} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#1A1422] truncate">{u.name}</div>
                    <div className="text-xs text-[#8B8794]">{ROLE_LABEL_MAP[u.role] || u.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
